import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import Sidebar from "@/components/Sidebar";
import { api } from "@/lib/api";
import { Hotel, Plus, Trash2, ArrowUp, ArrowDown, Check, X } from "lucide-react";

export default function AdminHotelCategories() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [editing, setEditing] = useState({}); // id -> { name, enabled }

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/hotel-categories");
      setRows(data || []);
    } catch (e) { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    const name = newName.trim();
    if (!name) return toast.error("Name required");
    try {
      await api.post("/admin/hotel-categories", { name, enabled: true });
      setNewName(""); toast.success("Added"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Add failed"); }
  };

  const save = async (r) => {
    const d = editing[r.id] || {};
    const name = (d.name ?? r.name).trim();
    const enabled = d.enabled ?? r.enabled;
    if (!name) return toast.error("Name required");
    try {
      await api.put(`/admin/hotel-categories/${r.id}`, { name, enabled });
      setEditing((p) => ({ ...p, [r.id]: undefined }));
      toast.success("Saved"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete "${r.name}"?`)) return;
    try {
      await api.delete(`/admin/hotel-categories/${r.id}`);
      toast.success("Deleted"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const move = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= rows.length) return;
    const next = [...rows];
    [next[idx], next[j]] = [next[j], next[idx]];
    setRows(next);
    try { await api.post("/admin/hotel-categories/reorder", { ids: next.map((x) => x.id) }); }
    catch { toast.error("Reorder failed"); load(); }
  };

  const setDraft = (id, k, v) => setEditing((p) => ({ ...p, [id]: { ...(p[id] || {}), [k]: v } }));
  const isDirty = (r) => {
    const d = editing[r.id]; if (!d) return false;
    return (d.name !== undefined && d.name !== r.name) || (d.enabled !== undefined && d.enabled !== r.enabled);
  };

  return (
    <div className="flex min-h-screen">
      <Sidebar role="admin" />
      <main className="flex-1 bg-[#FCFCFC] p-6 lg:p-10">
        <div className="max-w-[900px]">
          <div className="mb-6">
            <div className="overline text-emerald-700">Admin</div>
            <h1 className="font-editorial text-3xl mt-1 text-slate-900 flex items-center gap-3">
              <Hotel size={28} className="text-emerald-600" /> Hotel Categories
            </h1>
            <p className="text-sm text-slate-500 mt-2">Tune the hotel category picker used across Quotations, Vouchers and Customer preferences — no redeploy needed.</p>
          </div>

          <div className="bg-white border border-border rounded p-4 mb-5 flex gap-2">
            <input
              data-testid="hcat-new-name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") add(); }}
              placeholder="New category name (e.g. 7 Star, Glamping)"
              className="flex-1 border border-border rounded px-3 py-2 text-sm"
            />
            <button data-testid="hcat-add-btn" onClick={add} className="btn-bronze inline-flex items-center gap-1">
              <Plus size={14} /> <span className="text-xs uppercase tracking-widest">Add</span>
            </button>
          </div>

          <div className="bg-white border border-border rounded overflow-hidden">
            <div className="grid grid-cols-[40px_1fr_120px_140px_80px] px-4 py-2 text-[10px] uppercase tracking-widest text-slate-500 bg-slate-50 border-b border-border">
              <div>#</div><div>Name</div><div>Enabled</div><div>Reorder</div><div className="text-right">Actions</div>
            </div>
            {loading && <div className="p-6 text-center text-sm text-slate-400">Loading…</div>}
            {!loading && rows.length === 0 && <div className="p-6 text-center text-sm text-slate-400">No categories yet.</div>}
            {rows.map((r, i) => {
              const d = editing[r.id] || {};
              const name = d.name !== undefined ? d.name : r.name;
              const enabled = d.enabled !== undefined ? d.enabled : r.enabled;
              return (
                <div key={r.id} data-testid={`hcat-row-${r.id}`} className="grid grid-cols-[40px_1fr_120px_140px_80px] items-center px-4 py-2 border-b border-border last:border-b-0">
                  <div className="text-xs text-slate-400">{i + 1}</div>
                  <input
                    data-testid={`hcat-name-${r.id}`}
                    value={name}
                    onChange={(e) => setDraft(r.id, "name", e.target.value)}
                    className="border border-transparent hover:border-border focus:border-emerald-500 rounded px-2 py-1 text-sm bg-transparent"
                  />
                  <label className="inline-flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                    <input type="checkbox" data-testid={`hcat-enabled-${r.id}`} checked={!!enabled} onChange={(e) => setDraft(r.id, "enabled", e.target.checked)} />
                    {enabled ? "Visible" : "Hidden"}
                  </label>
                  <div className="flex items-center gap-1">
                    <button data-testid={`hcat-up-${r.id}`} onClick={() => move(i, -1)} disabled={i === 0}
                      className="p-1.5 border border-border rounded disabled:opacity-30 hover:border-emerald-500" title="Move up"><ArrowUp size={13} /></button>
                    <button data-testid={`hcat-down-${r.id}`} onClick={() => move(i, 1)} disabled={i === rows.length - 1}
                      className="p-1.5 border border-border rounded disabled:opacity-30 hover:border-emerald-500" title="Move down"><ArrowDown size={13} /></button>
                  </div>
                  <div className="flex items-center gap-1 justify-end">
                    {isDirty(r) && (
                      <>
                        <button data-testid={`hcat-save-${r.id}`} onClick={() => save(r)} className="p-1.5 border border-emerald-500 bg-emerald-50 text-emerald-700 rounded" title="Save"><Check size={13} /></button>
                        <button data-testid={`hcat-cancel-${r.id}`} onClick={() => setEditing((p) => ({ ...p, [r.id]: undefined }))} className="p-1.5 border border-border rounded" title="Cancel"><X size={13} /></button>
                      </>
                    )}
                    <button data-testid={`hcat-del-${r.id}`} onClick={() => remove(r)} className="p-1.5 border border-border rounded hover:border-red-500 hover:text-red-600" title="Delete"><Trash2 size={13} /></button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </main>
    </div>
  );
}
